// app/features/user-profile/components/UserProfilePageComponent.tsx
'use client';

import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Calendar, MessageCircle, Phone, User } from 'lucide-react';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';

import { HTFBadge } from '@/app/shared/components/feedback/HTFBadge';
import { HTFButton } from '@/app/shared/components/button/HTFButtonComponent';
import { LoadingIndicator } from '@/components/ui/loading-indicator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useUserProfileQueryHook } from '../services_and_hooks/useUserProfileQueryHook';

import { ClientDataSectionComponent } from './ClientDataSectionComponent';
import { UserConversationsSectionComponent } from './UserConversationsSectionComponent';

// Format phone number for better readability
const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.startsWith('55') && cleaned.length >= 12) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 10)} ${cleaned.slice(10)}`;
  }
  
  if (cleaned.length > 10) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
  }
  
  return phone;
};

// Format milestone/state for display
const formatState = (state: string | null): string => {
  if (!state) return 'Unknown';
  return state
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface UserProfilePageComponentProps {
  userId: string;
  orgId: string;
}

export function UserProfilePageComponent({ userId, orgId }: UserProfilePageComponentProps) {
  const router = useRouter();
  const { userProfile, isLoading, isError, error, refetch } = useUserProfileQueryHook(userId, orgId);
  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingIndicator text="Loading user profile..." />
      </div>
    );
  }

  if (isError || !userProfile) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="bg-surface rounded-xl p-8 max-w-md text-center">
          <User className="w-12 h-12 text-error mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2 text-gray-900">Error Loading Profile</h2>
          <p className="text-gray-600 mb-4">{error?.message || 'User not found.'}</p>
          <div className="flex gap-2 justify-center">
            <HTFButton onClick={() => router.back()} variant="secondary">
              Go Back
            </HTFButton>
            <HTFButton onClick={() => refetch()} variant="primary">
              Try Again
            </HTFButton>
          </div>
        </div>
      </div>
    );
  }

  // Get phone from identifiers (WhatsApp number) or fallback to extracted phone_number
  const primaryPhone = userProfile.identifiers?.[0]?.identifier_value || userProfile.phone_number;
  
  // Get full name - check multiple sources
  const fullName = userProfile.full_name || 
    userProfile.client_specific_data?.full_name ||
    userProfile.collected_data?.full_name;
  
  const displayName = fullName || 
    (primaryPhone ? formatPhoneNumber(primaryPhone) : 
    `User ${userProfile.id.slice(0, 8)}`);
  
  const primaryChannel = userProfile.identifiers?.[0] ? 'WhatsApp' : 'Unknown';

  // Get journey/conversation state from conversation_journey_states table
  const journeyMilestone = userProfile.journey_state?.current_milestone;
  const milestoneOrder = userProfile.journey_state?.milestone_order;

  // Get birth date from multiple sources
  const birthDate = userProfile.birth_date || 
    userProfile.client_specific_data?.birth_date ||
    userProfile.collected_data?.birth_date;

  return (
    <div className="w-full min-h-screen bg-surface-light">
      <div className="max-w-[90%] xl:max-w-[85%] mx-auto py-8 px-6">
        {/* Back Button */}
        <button 
          onClick={() => router.back()}
          className="flex items-center gap-2 text-gray-600 hover:text-teal transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Customers</span>
        </button>

        {/* Profile Header - Teal gradient background */}
        <div className="bg-gradient-to-r from-teal to-teal-dark rounded-xl mb-6 overflow-hidden">
          <div className="p-8">
            <div className="flex items-start justify-between">
              {/* Left: Name and details */}
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-bold text-white mb-3">{displayName}</h1>
                
                <div className="flex flex-wrap items-center gap-6 text-base text-white/90 font-extrabold">
                  {primaryPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-5 h-5" />
                      <span>{formatPhoneNumber(primaryPhone)}</span>
                    </div>
                  )}
                  
                  {birthDate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      <span>{new Date(birthDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    <span>{primaryChannel}</span>
                  </div>
                </div>
              </div>
              
              {/* Right: Journey State + Stats */}
              <div className="flex flex-col items-end gap-3 flex-shrink-0">
                {/* Journey State Pill - Purple */}
                {journeyMilestone && (
                  <span className="px-5 py-2 bg-purple text-black text-base font-bold rounded-full shadow-lg">
                    {milestoneOrder && <span>{milestoneOrder} </span>}
                    {formatState(journeyMilestone)}
                  </span>
                )}
                
                {/* Stats below the pill */}
                <div className="flex gap-6 text-white/90">
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">{userProfile.conversations.length}</span>
                    <span className="text-sm ml-1">conversations</span>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">
                      {userProfile.conversations.reduce((sum, c) => sum + c.message_count, 0)}
                    </span>
                    <span className="text-sm ml-1">messages</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs - Only 2 now: Overview (with conversations) and Client Data */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-beige-light rounded-lg p-1 mb-6">
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-teal data-[state=active]:text-white rounded-md px-4 py-2"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="client-data"
              className="data-[state=active]:bg-teal data-[state=active]:text-white rounded-md px-4 py-2"
            >
              Client Data
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Demographics + Activity Timeline */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Demographics */}
                <div className="bg-beige-light rounded-xl p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <User className="w-6 h-6 text-teal" />
                    Demographics
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3">
                      <span className="text-base text-gray-700">Full Name</span>
                      <span className="text-base font-semibold text-gray-900">
                        {fullName || <span className="text-gray-400 italic font-normal">Not collected</span>}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3">
                      <span className="text-base text-gray-700">Phone</span>
                      <span className="text-base font-semibold text-gray-900">
                        {primaryPhone ? formatPhoneNumber(primaryPhone) : <span className="text-gray-400 italic font-normal">Not collected</span>}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3">
                      <span className="text-base text-gray-700">Birth Date</span>
                      <span className="text-base font-semibold text-gray-900">
                        {birthDate 
                          ? new Date(birthDate).toLocaleDateString() 
                          : <span className="text-gray-400 italic font-normal">Not collected</span>}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Activity Timeline */}
                <div className="bg-beige-light rounded-xl p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <Calendar className="w-6 h-6 text-teal" />
                    Activity
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3">
                      <span className="text-base text-gray-700">First Contact</span>
                      <span className="text-base font-semibold text-gray-900">
                        {userProfile.first_seen 
                          ? formatDistanceToNow(new Date(userProfile.first_seen), { addSuffix: true })
                          : <span className="text-gray-400 italic font-normal">Unknown</span>}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3">
                      <span className="text-base text-gray-700">Last Activity</span>
                      <span className="text-base font-semibold text-gray-900">
                        {userProfile.last_seen 
                          ? formatDistanceToNow(new Date(userProfile.last_seen), { addSuffix: true })
                          : <span className="text-gray-400 italic font-normal">Unknown</span>}
                      </span>
                    </div>
                    
                    {userProfile.journey_state && (
                      <div className="flex justify-between items-center py-3">
                        <span className="text-base text-gray-700">Journey</span>
                        <HTFBadge variant="secondary" size="md" rounded="full">
                          {userProfile.journey_state.journey_id || 'Unknown'}
                        </HTFBadge>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Conversations List */}
              <UserConversationsSectionComponent 
                conversations={userProfile.conversations}
                orgId={orgId}
              />
            </div>
          </TabsContent>

          <TabsContent value="client-data">
            <ClientDataSectionComponent 
              clientSpecificData={userProfile.client_specific_data}
              collectedData={userProfile.collected_data}
              lastUpdated={userProfile.last_data_update}
              clientData={userProfile.client_data}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
